<template>
  <div class="wl-cam" :class="{ 'wl-cam--fpv': editor.viewMode.value === 'fpv' }">
    <div class="wl-cam__bar">
      <button type="button" class="wl-cam__swap" :title="editor.viewMode.value === 'fpv' ? '退出第一视角 (~)' : '主画面切换为第一视角 (~)'" @click="editor.setViewMode(editor.viewMode.value === 'fpv' ? 'follow' : 'fpv')"><SwapOutlined /></button>
      <button type="button" class="wl-cam__tab" :class="{ active: aircraft.lens === 'wide' }" @click="editor.setAircraftLens('wide')">广角 1X <kbd>1</kbd></button>
      <button v-if="hasZoom" type="button" class="wl-cam__tab" :class="{ active: aircraft.lens === 'zoom' }" @click="editor.setAircraftLens('zoom')">变焦 7X <kbd>2</kbd></button>
      <span v-if="hasIr" class="wl-cam__ir">IR</span>
    </div>
    <div class="wl-cam__frame" @wheel.prevent="onWheel">
      <div class="wl-cam__sky" :style="{ height: horizon + '%' }" />
      <div class="wl-cam__ground" :style="{ top: horizon + '%' }">
        <div class="wl-cam__grid" :style="{ backgroundPositionY: gridOffset + 'px' }" />
      </div>
      <div class="wl-cam__horizon" :style="{ top: horizon + '%' }" />
      <div class="wl-cam__tape"><span v-for="mark in tape" :key="mark.key" :style="{ left: mark.left + '%' }" :class="{ cardinal: mark.cardinal }">{{ mark.label }}</span></div>
      <span class="wl-cam__corner tl" /><span class="wl-cam__corner tr" /><span class="wl-cam__corner bl" /><span class="wl-cam__corner br" />
      <div class="wl-cam__reticle"><span /><b>{{ range }}</b></div>
      <div class="wl-cam__zoom">
        <span v-for="level in zoomLevels" :key="level" :class="{ active: level === activeZoom }">{{ level }}X</span>
      </div>
      <div class="wl-cam__readout">
        <span>{{ headingLabel(aircraft.heading) }}</span>
        <span>云台 {{ Math.round(aircraft.gimbalPitch) }}°</span>
      </div>
      <p class="wl-cam__note">模拟画面：按姿态示意，非真实图像。滚轮调整云台俯仰。</p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { SwapOutlined } from '@ant-design/icons-vue'
import { cameraFootprint, cameraSpec, headingLabel } from '/@/utils/wayline-editor'
import { useWaylineEditor } from './editor-context'

const editor = useWaylineEditor()
const draft = editor.draft
const aircraft = editor.aircraft
const hasZoom = computed(() => Boolean(editor.payloadProfile.value?.lenses.includes('zoom')))
const hasIr = computed(() => Boolean(editor.payloadProfile.value?.lenses.includes('ir')))
const zoomLevels = [112, 56, 14, 7, 3]
const activeZoom = computed(() => aircraft.lens === 'zoom' ? 7 : 3)
/** Horizon position in percent of the frame; looking down pushes the sky out of view. */
const horizon = computed(() => Math.max(-20, Math.min(120, 50 - aircraft.gimbalPitch * (aircraft.lens === 'zoom' ? 3.2 : 1.1))))
const gridOffset = computed(() => Math.round(aircraft.height * 3) % 40)
const agl = editor.aircraftAgl
const range = computed(() => {
  if (aircraft.gimbalPitch >= -1) return '∞'
  const camera = cameraSpec(draft)
  const view = cameraFootprint(aircraft, agl.value, camera, aircraft.lens === 'zoom' ? camera.focalLength * 7 : camera.focalLength)
  return `${view.aimRange.toFixed(1)} m`
})
const tape = computed(() => {
  const span = aircraft.lens === 'zoom' ? 24 : 84
  const marks: Array<{ key: string; label: string; left: number; cardinal: boolean }> = []
  for (let degrees = -180; degrees < 540; degrees += 15) {
    const offset = degrees - aircraft.heading
    if (Math.abs(offset) > span / 2) continue
    const value = ((degrees % 360) + 360) % 360
    const cardinal = value % 90 === 0
    marks.push({ key: String(degrees), label: cardinal ? ['N', 'E', 'S', 'W'][value / 90] : value % 30 === 0 ? String(value) : '·', left: 50 + offset / span * 100, cardinal })
  }
  return marks
})

function onWheel (event: WheelEvent) {
  editor.setGimbalPitch(aircraft.gimbalPitch - Math.sign(event.deltaY) * 3)
}
</script>

<style lang="scss">
.wl-cam {
  width: 300px; border-radius: 6px; overflow: hidden; background: #151618; border: 1px solid #35373c; color: #fff; box-shadow: 0 6px 18px #0009;
  &--fpv { border-color: #2d8cf0; }
  &__bar { display: flex; align-items: center; gap: 4px; height: 28px; padding: 0 6px; background: #1b1c1f; }
  &__swap { width: 24px; height: 22px; border: 0; border-radius: 3px; background: transparent; color: #fff; cursor: pointer; }
  &__swap:hover { background: #ffffff1f; }
  &__tab { flex: 1; height: 22px; border: 0; border-radius: 3px; background: #2c2d31; color: #c9ccd2; cursor: pointer; font-size: 11px; }
  &__tab.active { background: #2d8cf0; color: #fff; }
  &__tab kbd { font-family: inherit; font-size: 10px; opacity: 0.8; }
  &__ir { padding: 0 6px; border-radius: 3px; background: #4d1c1c; color: #ff8f8f; font-size: 10px; font-weight: 700; }
  &__frame { position: relative; height: 168px; overflow: hidden; background: #3b5a72; }
  &__sky { position: absolute; left: 0; right: 0; top: 0; background: linear-gradient(#6fa8dc, #a8c8e8 70%, #d9e6f2); }
  &__ground { position: absolute; left: 0; right: 0; bottom: -60%; background: linear-gradient(#6d7f5a, #4b5b3d 60%, #3a4530); overflow: hidden; }
  &__grid { position: absolute; inset: 0; background-image: linear-gradient(#ffffff14 1px, transparent 1px), linear-gradient(90deg, #ffffff14 1px, transparent 1px); background-size: 40px 40px; transform: perspective(160px) rotateX(55deg); transform-origin: top; }
  &__horizon { position: absolute; left: 0; right: 0; height: 1px; background: #ffffffa8; }
  &__tape { position: absolute; top: 4px; left: 0; right: 0; height: 14px; font-size: 9px; color: #fff; text-shadow: 0 1px 2px #000; }
  &__tape span { position: absolute; transform: translateX(-50%); }
  &__tape span.cardinal { font-weight: 700; color: #ffd166; }
  &__corner { position: absolute; width: 18px; height: 18px; border: 2px solid #ff4d4f; }
  &__corner.tl { top: 22px; left: 26px; border-right: 0; border-bottom: 0; }
  &__corner.tr { top: 22px; right: 26px; border-left: 0; border-bottom: 0; }
  &__corner.bl { bottom: 22px; left: 26px; border-right: 0; border-top: 0; }
  &__corner.br { bottom: 22px; right: 26px; border-left: 0; border-top: 0; }
  &__reticle { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); display: flex; flex-direction: column; align-items: center; gap: 2px; }
  &__reticle span { width: 22px; height: 22px; border: 2px solid #19be6b; border-radius: 3px; box-shadow: 0 0 0 1px #0006; }
  &__reticle b { font-size: 11px; font-weight: 500; text-shadow: 0 1px 2px #000; }
  &__zoom { position: absolute; right: 6px; top: 22px; bottom: 22px; display: flex; flex-direction: column; justify-content: space-between; font-size: 9px; color: #d5d8de; text-shadow: 0 1px 2px #000; }
  &__zoom span.active { color: #ffd166; font-weight: 700; }
  &__readout { position: absolute; left: 8px; bottom: 6px; display: flex; gap: 10px; font-size: 10px; text-shadow: 0 1px 2px #000; }
  &__note { position: absolute; right: 30px; bottom: 5px; margin: 0; font-size: 9px; color: #ffffffb0; text-shadow: 0 1px 2px #000; }
}
</style>
